import React, { useEffect, useRef, useState } from "react";
import get from "lodash/get";
import { useSelector } from "react-redux";
import { Link } from "@quintype/components";
import { trackCtaClick } from "../../../ga-utils";

import { MagazineCoverImageCard } from "../../../arrow/components/Atoms/MagazineCoverImage";
import { SectionPage } from "../section";
import { CollectionName } from "../../../arrow";
import { getMagazinesTimeStamp } from "../../../arrow/utils/timestamp-utils";
import { Button } from "../../atoms";
import { LatestMagazineIssues } from "../../collection-templates/latest-magazine-issues";
import ListingAdSlot from "../../molecules/ListingAdSlot";
import { useDeviceType } from "../../customHooks/useDeviceType";

import "./magazine-issue-landing-page.m.css";

export const MagazineIssueLandingPage = (props) => {
  const issue = props.data.magazineIssue.data;
  const coverImageSlug = get(issue, ["metadata", "cover-image", "cover-image-s3-key"], "");
  const { "issue-date": issueDate, summary, id } = issue;
  const formattedTimestamp = getMagazinesTimeStamp(issueDate);

  const qtConfig = useSelector((state) => get(state, ["qt", "config"]));
  const showEmagazineButton = get(qtConfig, ["publisher-attributes", "show_read_emagazine_link"], false);
  const readEmagazineLink = get(qtConfig, ["publisher-attributes", "read_emagazine_link"], "");

  const bioRef = useRef(null);
  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";
  const [readMoreClicked, setReadMoreClicked] = useState(false);
  const [readMoreVisible, setReadMoreVisible] = useState(false);
  const isAdfreePage = get(qtConfig, ["subscriber"]) === "true";

  const expandBio = () => {
    if (typeof window !== "undefined") {
      trackCtaClick({
        linkText: "read more",
        linkCategory: "magazine",
        pageType: "magazines-issue-landing-page",
        location: window.location,
      });
    }
    setReadMoreClicked(true);
  };

  // Track "Read Full E-Magazine" CTA click
  const trackReadEmagazineClick = () => {
    if (typeof window !== "undefined") {
      trackCtaClick({
        linkText: "read full e-magazine",
        linkUrl: readEmagazineLink,
        linkCategory: "magazine",
        pageType: "magazines-issue-landing-page",
        location: window.location,
      });
    }
  };

  useEffect(() => {
    if (bioRef.current) {
      const bioHeight = window?.getComputedStyle(bioRef.current);
      const getLineHeight = parseFloat(bioHeight.lineHeight);
      const lineHeightForThree = getLineHeight * 3;
      const contentHeight = bioRef.current.scrollHeight;
      bioRef.current.style.height = `${lineHeightForThree}px`;
      bioRef.current.style.overflow = "hidden";

      // Wait for the next render cycle to check scrollHeight
      setTimeout(() => {
        if (bioRef.current?.scrollHeight > bioRef.current?.clientHeight) {
          setReadMoreVisible(true);
        }
      }, 0);

      if (readMoreClicked) {
        bioRef.current.style.height = `${contentHeight}px`;
      }
    }
    return () => {
      if (bioRef?.current) {
        bioRef.current.style.overflow = "visible";
      }
    };
  }, [readMoreClicked, isDesktop]);

  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "dl_page_info",
      cms_author_name: "NA",
      byline: "NA",
      category: "magazine",
      sub_category: formattedTimestamp.replace(" ", "-") || "NA",
      content_id: "NA",
      template_name: "edition",
      publish_date: "NA",
      publish_time: "NA",
      login_status: "not-logged-in", // will be changed
      subscription_status: "not-subscribed", // will be changed
      ad_free: false, // will be changed
      login_wall_enabled: false,
      platform: window.innerWidth >= 768 ? "Desktop" : "Mobile",
    });
  }, []);

  return (
    <div className="container" styleName="magazine-issue-landing-page">
      <div styleName="magazine-issue">
        <div styleName="image-wrapper">
          <MagazineCoverImageCard collection={issue} config={{ useLink: false }} isHomepage={true} />
        </div>
        <div styleName="content-wrapper">
          <div styleName="content">
            <div styleName={`time `} data-test-id="widget-summary">
              {formattedTimestamp}
            </div>
            <CollectionName collection={issue} navigate={false} />
            {isDesktop && (
              <div style={{ position: "relative" }} styleName={`summary `} data-test-id="widget-summary">
                <span ref={bioRef} styleName={readMoreClicked ? "" : "summary-text"}>
                  {summary}
                </span>
                {!readMoreClicked && readMoreVisible && (
                  <button onClick={expandBio} styleName="read-more-btn">
                    Read More
                  </button>
                )}
              </div>
            )}
            {showEmagazineButton && (
              <Link href={readEmagazineLink} disableAjaxLinks={true} onMouseDown={trackReadEmagazineClick}>
                <div styleName="read-more">
                  <Button customStyleName={"bg-brand-primary"}>Read Full E-Magazine</Button>
                </div>
              </Link>
            )}
          </div>
        </div>
        {!isDesktop && (
          <div style={{ position: "relative" }} styleName={`summary `} data-test-id="widget-summary">
            <span ref={bioRef} styleName={readMoreClicked ? "" : "summary-text"}>
              {summary}
            </span>
            {!readMoreClicked && readMoreVisible && (
              <button onClick={expandBio} styleName="read-more-btn">
                Read More
              </button>
            )}
          </div>
        )}
      </div>
      {!isAdfreePage && (
        <ListingAdSlot
          adType="below_button"
          pageType="magazines-issue-landing-page"
          adStyleName="ad-slot-size-300x250"
          index={2}
          adSlotConfigName={"mRec_ad"}
        />
      )}
      <SectionPage
        {...props.data}
        pageType={props.pageType}
        showAllStories={true}
        isMagazinePage={true}
        magazineIssueTitle={`All Magazine Stories - ${formattedTimestamp} Issue`}
      />
      <LatestMagazineIssues skipMagazineId={id} pageType={props.pageType} />
    </div>
  );
};
